/**
 * MfaPage (`/mfa`, RF-M11-001, RN-M1-001) — código TOTP de 6 dígitos.
 *
 * Alcançada após o login quando o Cognito exige MFA (todo perfil, ADR-052). Conclui
 * o desafio via `confirmMfa`; erro genérico (RN-GLOBAL-004). Concluído, redireciona
 * ao destino pretendido (ou /escolas).
 *
 * Foco (spec `ajustes-login-e-visualizador`): o campo do código abre com o foco e, se o
 * código for recusado, o foco volta a ele com o texto selecionado — digita-se direto.
 */

import { FormEvent, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth, useAuthActions } from '../contexts/AuthContext';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';

interface LocationState {
  readonly from?: string;
}

export function MfaPage(): JSX.Element {
  const { confirmMfa } = useAuthActions();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const whiteLabel = useWhiteLabel();

  const [code, setCode] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const campoCodigo = useRef<HTMLInputElement>(null);

  const destino = (location.state as LocationState | null)?.from ?? '/escolas';

  // Já autenticado (MFA concluído): não faz sentido permanecer na tela de MFA.
  if (auth.user) {
    return <Navigate to={destino} replace />;
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await confirmMfa(code.trim());
      navigate(destino, { replace: true });
    } catch {
      setErro('Código de verificação inválido ou expirado.');
      campoCodigo.current?.focus();
      campoCodigo.current?.select();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-page" style={{ ['--cor-primaria' as string]: whiteLabel.primaryColor }}>
      <form className="auth-card" onSubmit={onSubmit} aria-labelledby="mfa-title">
        <h1 id="mfa-title" className="auth-title">
          Verificação em duas etapas
        </h1>
        <p className="auth-subtitle">
          Informe o código de 6 dígitos do seu aplicativo autenticador.
        </p>

        <label className="field">
          <span>Código</span>
          <input
            ref={campoCodigo}
            autoFocus
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </label>

        {erro && (
          <p className="form-error" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={enviando}>
          {enviando ? 'Verificando…' : 'Verificar'}
        </button>
      </form>
    </div>
  );
}
